import React, { HTMLAttributes, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import classNames from "classnames";
import * as Icons from "Components/Icons";
import { Button, Modal, ModalBody } from "Components/ReactstrapSlim";
import { HeaderLogo } from "Components/Layout";
import { IBrandedBannerViewModel, PortalClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { brandingSelector } from "Utils/selectors";

const POPUP_COOKIE_NAME = "portalbannerhash";

export type PortalBannerProps = HTMLAttributes<HTMLDivElement> & {
   accountId: Guid,
   fixed?: boolean
}

export const Banner: React.FC<PortalBannerProps> = ({ accountId, fixed, className, ...rest }) => {
   const [bannerSettings, setBannerSettings] = useState<IBrandedBannerViewModel | undefined>(undefined);
   const [isToggled, setIsToggled] = useState<boolean>(false);
   const [showPopup, setShowPopup] = useState<boolean>(false);
   const portalClient = useApiClient(PortalClient);   
   const [cookies, setCookie] = useCookies([POPUP_COOKIE_NAME]);
   const branding = useSelector(brandingSelector);

   const toggleBanner = (ev: React.SyntheticEvent) => {      
      ev.preventDefault();
      setIsToggled((wasToggled) => !wasToggled);
   }

   const showBannerPopup = (ev?: React.SyntheticEvent) => {
      if(ev)
         ev.preventDefault();

      setShowPopup(true);
   }   

   const hideBannerPopup = () => {
      setShowPopup(false);

      if (bannerSettings)
         setCookie(POPUP_COOKIE_NAME, bannerSettings.popupHash, {path: "/"});
   }

   useEffect(() => {
      const abortController = new AbortController();
      portalClient.banner(accountId, abortController.signal)
         .then(data => setBannerSettings(data));
      return function cleanup() {
         abortController.abort();
      }
   }, [])

   useEffect(() => {
      if (bannerSettings
         && bannerSettings.enable
         && bannerSettings.forcePopup
         && bannerSettings.popupContent
         && (!cookies[POPUP_COOKIE_NAME] || cookies[POPUP_COOKIE_NAME] != bannerSettings.popupHash)) {
         showBannerPopup();
      }
   }, [bannerSettings])

   const collapseDetails = () => {
      setIsToggled(false);
   }

   useEffect(() => {
      document.addEventListener('fullscreenchange', collapseDetails);
      document.addEventListener('webkitfullscreenchange', collapseDetails);

      return () => {
         document.removeEventListener('fullscreenchange', collapseDetails);
         document.removeEventListener('webkitfullscreenchange', collapseDetails);
      }
   })

   if (!bannerSettings || !bannerSettings.enable)
      return null;
   
   return (
      <div className={classNames("portal-banner", className, {
         "portal-banner--fixed": fixed,
         "portal-banner--toggled": isToggled
      })} >
         <a href="#" className="portal-banner-toggle" onClick={toggleBanner}>
            <div className="portal-banner-toggle-info"><Icons.Info size={20} /></div>
            <div className="portal-banner-toggle-text">{bannerSettings.title}</div>
            <div className="portal-banner-toggle-icon"><Icons.Dropdown size={20} /></div>
         </a>
         <div className="portal-banner-subtitle">
            <span dangerouslySetInnerHTML={{ __html: bannerSettings.subtitle || "" }}></span>
            {bannerSettings.popupContent ? <a href="#" className="portal-banner-learn-more" onClick={showBannerPopup}>Learn more</a> : null}
         </div>
         {<Modal className="modal-lg portal-banner-modal" isOpen={showPopup} centered>
            <ModalBody>
               <div className="portal-banner-modal-header">
                  <HeaderLogo branding={branding} />
               </div>
               <div className="portal-banner-modal-body">
                  {bannerSettings.popupTitle && <div className="portal-banner-modal-title">{bannerSettings.popupTitle}</div>}
                  <div className="portal-banner-modal-content" dangerouslySetInnerHTML={{ __html: bannerSettings.popupContent || "" }}></div>
                  <div className="portal-banner-modal-cta">
                     <Button size="lg" color="primary" onClick={hideBannerPopup}>Got it!</Button>
                  </div>
               </div>               
            </ModalBody>
         </Modal>}
      </div>
      )   
}